import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  Autocomplete,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { STANDARD_TAGS_BY_GROUP } from "./standardTags";

export interface ImageOrderingValue {
  ImageOrder?: string;
  [key: string]: string | number | undefined;
}

interface Props {
  value: ImageOrderingValue;
  onChange: (next: ImageOrderingValue) => void;
  groupSuggestions?: string[];
}

interface Token {
  group: string;
  tag: string;
  required: boolean;
  wildcard: boolean;
}

const DEFAULT_GROUP_SUGGESTIONS = [
  "exterior",
  "featured",
  "manual",
  "pano",
  "undercarriage",
  "exteriorOriginal",
  "exteriorProcessed",
];

const parseToken = (raw: string): Token => {
  let s = raw.trim();
  let required = false;
  let wildcard = false;
  if (s.endsWith("!")) {
    required = true;
    s = s.slice(0, -1);
  } else if (s.endsWith("*")) {
    wildcard = true;
    s = s.slice(0, -1);
  }
  const idx = s.indexOf("_");
  if (idx === -1) return { group: s, tag: "", required, wildcard };
  return { group: s.slice(0, idx), tag: s.slice(idx + 1), required, wildcard };
};

const serializeToken = (t: Token): string => {
  const base = `${t.group}_${t.tag}`;
  if (t.required) return `${base}!`;
  if (t.wildcard) return `${base}*`;
  return base;
};

const parseImageOrder = (s: string | undefined): Token[] => {
  if (!s) return [];
  return s
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map(parseToken);
};

const serializeImageOrder = (tokens: Token[]): string =>
  tokens.map(serializeToken).join(",");

const uniqueGroups = (tokens: Token[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tokens) {
    if (t.group && !seen.has(t.group)) {
      seen.add(t.group);
      out.push(t.group);
    }
  }
  return out;
};

const ImageOrderingEditor: React.FC<Props> = ({
  value,
  onChange,
  groupSuggestions,
}) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [fallbackModalOpen, setFallbackModalOpen] = useState(false);

  const tokens = useMemo(() => parseImageOrder(value.ImageOrder), [value.ImageOrder]);
  const groups = useMemo(() => uniqueGroups(tokens), [tokens]);
  const suggestions = useMemo(
    () => Array.from(new Set([...(groupSuggestions || []), ...DEFAULT_GROUP_SUGGESTIONS])),
    [groupSuggestions],
  );

  const emitTokens = (next: Token[]) => {
    onChange({ ...value, ImageOrder: serializeImageOrder(next) });
  };

  const updateToken = (idx: number, patch: Partial<Token>) => {
    const next = tokens.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    emitTokens(next);
  };

  const moveToken = (idx: number, delta: number) => {
    const target = idx + delta;
    if (target < 0 || target >= tokens.length) return;
    const next = [...tokens];
    [next[idx], next[target]] = [next[target], next[idx]];
    emitTokens(next);
  };

  const removeToken = (idx: number) => {
    emitTokens(tokens.filter((_, i) => i !== idx));
  };

  const addToken = () => {
    emitTokens([...tokens, { group: "", tag: "", required: false, wildcard: false }]);
  };

  const addStandardTags = (group: string) => {
    const tags = STANDARD_TAGS_BY_GROUP[group] || [];
    const existing = new Set(tokens.map((t) => `${t.group}_${t.tag}`));
    const additions: Token[] = tags
      .filter((tag) => !existing.has(`${group}_${tag}`))
      .map((tag) => ({ group, tag, required: false, wildcard: false }));
    if (!additions.length) return;
    emitTokens([...tokens, ...additions]);
  };

  const quickAddGroups = groupSuggestions && groupSuggestions.length > 0
    ? groupSuggestions.filter((g) => g in STANDARD_TAGS_BY_GROUP)
    : Object.keys(STANDARD_TAGS_BY_GROUP);

  const updateFallback = (group: string, fallback: string) => {
    onChange({ ...value, [`${group}_GroupFallback`]: fallback });
  };

  const updateRequiredCount = (group: string, count: number) => {
    const next: ImageOrderingValue = { ...value };
    const key = `${group}_RequiredCount`;
    if (!count || count <= 0) {
      delete next[key];
    } else {
      next[key] = count;
    }
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" sx={{ color: accent, fontWeight: 600 }}>
            Image Order
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setOrderModalOpen(true)}
            sx={{ textTransform: "none", color: accent, borderColor: accent }}
          >
            Edit
          </Button>
        </Stack>

        <Box
          sx={{
            p: 1.25,
            borderRadius: 1,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.025)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            minHeight: 56,
            color: value.ImageOrder ? "text.primary" : "text.disabled",
          }}
        >
          {value.ImageOrder || "(empty — click Edit to add tokens)"}
        </Box>
      </Box>

      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" sx={{ color: accent, fontWeight: 600 }}>
            Group Fallbacks
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setFallbackModalOpen(true)}
            disabled={groups.length === 0}
            sx={{ textTransform: "none", color: accent, borderColor: accent }}
          >
            Edit
          </Button>
        </Stack>

        <Box
          sx={{
            p: 1.25,
            borderRadius: 1,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
            backgroundColor: isDark
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.025)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12.5,
            lineHeight: 1.55,
            minHeight: 56,
          }}
        >
          {groups.length === 0 ? (
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", fontStyle: "italic" }}
            >
              (add tokens in Image Order to configure fallbacks)
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {groups.map((g) => {
                const fallback = (value[`${g}_GroupFallback`] as string) || "";
                const requiredCount = Number(value[`${g}_RequiredCount`] ?? 0);
                return (
                  <Box
                    key={g}
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      color: "text.primary",
                    }}
                  >
                    <Box sx={{ fontWeight: 600 }}>{g}</Box>
                    <Box sx={{ color: "text.secondary" }}>→</Box>
                    <Box
                      sx={{
                        color: fallback ? "text.primary" : "text.disabled",
                        wordBreak: "break-word",
                      }}
                    >
                      {fallback || "(no fallback set)"}
                    </Box>
                    {requiredCount > 0 && (
                      <Box sx={{ color: accent, fontWeight: 600 }}>
                        [required: {requiredCount}]
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: accent, fontWeight: 600 }}>
          Edit Image Order
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 2 }}
          >
            Order in which placeholder slots are filled. Toggle{" "}
            <Chip label="!" size="small" sx={{ height: 18, fontSize: 11 }} />{" "}
            for required (won&apos;t send to syndicator if missing) or{" "}
            <Chip label="*" size="small" sx={{ height: 18, fontSize: 11 }} />{" "}
            to include all images that match this tag, not just the first
            (e.g. every <code>manual_Unassigned</code> image).
          </Typography>

          {quickAddGroups.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mb: 0.75,
                  fontWeight: 600,
                }}
              >
                Quick add standard tags
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {quickAddGroups.map((g) => {
                  const all = STANDARD_TAGS_BY_GROUP[g] || [];
                  const existing = new Set(
                    tokens.map((t) => `${t.group}_${t.tag}`),
                  );
                  const remaining = all.filter(
                    (tag) => !existing.has(`${g}_${tag}`),
                  ).length;
                  return (
                    <Button
                      key={g}
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      disabled={remaining === 0}
                      onClick={() => addStandardTags(g)}
                      sx={{
                        textTransform: "none",
                        color: accent,
                        borderColor: accent,
                      }}
                    >
                      {g} ({remaining}/{all.length})
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Stack spacing={1}>
            {tokens.map((t, idx) => (
              <Grid container key={idx} spacing={1} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={suggestions}
                    value={t.group}
                    onInputChange={(_, v) => updateToken(idx, { group: v || "" })}
                    renderInput={(params) => (
                      <TextField {...params} label="Group" placeholder="exterior" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Tag"
                    placeholder="Left-Front"
                    value={t.tag}
                    onChange={(e) => updateToken(idx, { tag: e.target.value })}
                  />
                </Grid>
                <Grid item xs="auto">
                  <Tooltip title="Required — won't send to syndicator if missing">
                    <ToggleButton
                      value="required"
                      size="small"
                      selected={t.required}
                      onChange={() =>
                        updateToken(idx, {
                          required: !t.required,
                          wildcard: !t.required ? false : t.wildcard,
                        })
                      }
                      sx={{
                        px: 1.5,
                        minWidth: 40,
                        fontWeight: 700,
                        "&.Mui-selected": { color: accent, borderColor: accent },
                      }}
                    >
                      !
                    </ToggleButton>
                  </Tooltip>
                </Grid>
                <Grid item xs="auto">
                  <Tooltip title="Include every image with this tag, not just the first">
                    <ToggleButton
                      value="wildcard"
                      size="small"
                      selected={t.wildcard}
                      onChange={() =>
                        updateToken(idx, {
                          wildcard: !t.wildcard,
                          required: !t.wildcard ? false : t.required,
                        })
                      }
                      sx={{
                        px: 1.5,
                        minWidth: 40,
                        fontWeight: 700,
                        "&.Mui-selected": { color: accent, borderColor: accent },
                      }}
                    >
                      *
                    </ToggleButton>
                  </Tooltip>
                </Grid>
                <Grid item xs="auto">
                  <IconButton
                    size="small"
                    onClick={() => moveToken(idx, -1)}
                    disabled={idx === 0}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </Grid>
                <Grid item xs="auto">
                  <IconButton
                    size="small"
                    onClick={() => moveToken(idx, 1)}
                    disabled={idx === tokens.length - 1}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Grid>
                <Grid item xs="auto">
                  <IconButton size="small" onClick={() => removeToken(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Stack>

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addToken}
            sx={{ mt: 1.5, textTransform: "none", color: accent }}
          >
            Add row
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOrderModalOpen(false)}
            sx={{ textTransform: "none", color: accent, fontWeight: 600 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fallbackModalOpen}
        onClose={() => setFallbackModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: accent, fontWeight: 600 }}>
          Edit Group Fallbacks
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 2 }}
          >
            For each group used in Image Order: a comma-delimited fallback list
            (first available wins), and an optional minimum required count
            (0 = no minimum).
          </Typography>

          <Stack spacing={1.5}>
            {groups.map((g) => {
              const fallback = (value[`${g}_GroupFallback`] as string) || "";
              const requiredCount = Number(value[`${g}_RequiredCount`] ?? 0);
              return (
                <Grid container key={g} spacing={1} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Chip
                      label={g}
                      size="small"
                      sx={{
                        fontFamily: "ui-monospace, Menlo, monospace",
                        fontWeight: 600,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Fallback list"
                      placeholder="imageBgrExterior,imageCrop,imageExterior"
                      value={fallback}
                      onChange={(e) => updateFallback(g, e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Required count"
                      value={requiredCount}
                      onChange={(e) =>
                        updateRequiredCount(g, parseInt(e.target.value, 10) || 0)
                      }
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFallbackModalOpen(false)}
            sx={{ textTransform: "none", color: accent, fontWeight: 600 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ImageOrderingEditor;
